import { useEffect, useRef, type JSX } from 'react';
import gsap from 'gsap';
import { prefersReducedMotion } from '../preferences/reducedMotion.js';
import './GameEndResetFade.css';

interface GameEndResetFadeProps {
  /** Flip to `true` once the server-side phase change has actually been observed
   *  (router.phase === Lobby). The wash then starts its fade-out. Keeping the
   *  fade-out gated on real phase observation avoids a flash where the wash
   *  dies while GameEnd is still mounted underneath. */
  fadeOut: boolean;
  /** Fires when the wash reaches full opacity. Caller dispatches the
   *  server-side Back-to-Lobby action here — never on click — so the user sees
   *  a clean fade *before* the room state churns. */
  onPeak: () => void;
  /** Fires when the wash has fully dissipated. Caller unmounts the component. */
  onComplete: () => void;
}

/**
 * GameEndResetFade — the GameEnd → Lobby reset wash. A momentary fade to warm
 * `--susdle-ink-deep` (NEVER `#000`) that signals the round is over and a new
 * one begins. Cousin of the stalemate Lights-Out: same warm-ink family, but a
 * *transition*, not a *surface*. Not part of the Drenched Exception Register
 * (those are screens you live on; this is opacity 0→1→0).
 *
 * Three-stage lifecycle:
 *   1. Cover  — opacity 0→1 over 1100ms (power2.in). At peak, `onPeak` fires
 *               and the caller dispatches the server-side reset. While we
 *               wait for the server, we hold at full opacity.
 *   2. Hold   — opacity 1. Ends when `fadeOut` prop flips to true (the
 *               caller's signal that the Lobby phase has actually arrived).
 *   3. Reveal — opacity 1→0 over 1500ms (power2.out). At end, `onComplete`
 *               fires and the caller unmounts the component. The fresh Lobby
 *               emerges slowly from beneath the dissipating dark — long
 *               enough that the page feels like dawn breaking on the room.
 *
 * Total wall-clock: ~2.6s + however long the server takes to apply the
 * phase change ≈ ~3s end-to-end. The pace is deliberate: a reset is a
 * sanctioned beat, not an instant flip, so the user feels the room being
 * cleared and a new round starting.
 *
 * Reduced motion: the wash is suppressed entirely; `onPeak` fires next tick,
 * and once `fadeOut` is observed `onComplete` fires next tick. Net effect:
 * Back to Lobby jumps straight to a fresh Lobby with no intermediate frames.
 *
 * Cold-arrival rule: this component is only mounted by the *live* reset
 * gesture (a host click). A player who arrives at the Lobby mid-reset
 * (refresh, reconnect, late join) lands straight on the settled fresh
 * Lobby — they never see the wash because the App's local resetting state
 * was never set on their client.
 */
export function GameEndResetFade({
  fadeOut,
  onPeak,
  onComplete,
}: GameEndResetFadeProps): JSX.Element {
  const washRef = useRef<HTMLDivElement>(null);
  const peakFiredRef = useRef(false);
  const outFiredRef = useRef(false);
  const inTweenRef = useRef<gsap.core.Tween | null>(null);
  const outTweenRef = useRef<gsap.core.Tween | null>(null);

  /* Callbacks live in refs so the cover effect's dependency array stays
     empty — the cover tween must run EXACTLY ONCE per mount. With `onPeak`
     as a direct dep, any new callback identity from the parent (a snapshot
     render, the setResetting('covered') render) re-fires the effect, and
     gsap.fromTo's immediateRender snaps the wash back to opacity 0 —
     guaranteed at the exact moment the fresh Lobby snapshot arrives. Same
     pattern as GameEndBurnCanvas. */
  const onPeakRef = useRef(onPeak);
  const onCompleteRef = useRef(onComplete);
  onPeakRef.current = onPeak;
  onCompleteRef.current = onComplete;

  /* Cover beat: run once on mount. Reduced motion bails to instant. The peak
     handler must be idempotent (we guard with peakFiredRef) because the
     fade-out effect below could race with it on a fast server response. */
  useEffect(() => {
    const wash = washRef.current;
    if (!wash) return;
    const reduced = prefersReducedMotion();

    if (reduced) {
      gsap.set(wash, { opacity: 1 });
      const id = window.setTimeout(() => {
        if (!peakFiredRef.current) { peakFiredRef.current = true; onPeakRef.current(); }
      }, 0);
      return () => { window.clearTimeout(id); };
    }

    inTweenRef.current = gsap.fromTo(
      wash,
      { opacity: 0 },
      {
        opacity: 1,
        duration: 1.10,
        ease: 'power2.in',
        onComplete: () => {
          if (!peakFiredRef.current) { peakFiredRef.current = true; onPeakRef.current(); }
        },
      },
    );

    return () => { inTweenRef.current?.kill(); };
  }, []);

  /* Reveal beat: fire when `fadeOut` flips true (the caller's signal that
     the Lobby phase has arrived). If we're somehow still mid-cover when this
     fires (server response faster than the cover beat — unlikely but
     possible), the cover tween is killed and we start from current opacity. */
  useEffect(() => {
    if (!fadeOut) return;
    const wash = washRef.current;
    if (!wash) return;
    const reduced = prefersReducedMotion();

    if (reduced) {
      gsap.set(wash, { opacity: 0 });
      const id = window.setTimeout(() => {
        if (!outFiredRef.current) { outFiredRef.current = true; onCompleteRef.current(); }
      }, 0);
      return () => { window.clearTimeout(id); };
    }

    inTweenRef.current?.kill();
    /* Belt-and-braces: if the cover tween hadn't reached its onComplete by
       the time fadeOut fires (cover killed early), peak still needs to have
       fired (the server action depends on it). The button click → setResetting
       → mount path means peak should always fire before the phase observation
       gets back to flip fadeOut, but guard anyway. */
    if (!peakFiredRef.current) { peakFiredRef.current = true; onPeakRef.current(); }

    outTweenRef.current = gsap.to(wash, {
      opacity: 0,
      duration: 1.50,
      ease: 'power2.out',
      onComplete: () => {
        if (!outFiredRef.current) { outFiredRef.current = true; onCompleteRef.current(); }
      },
    });

    return () => { outTweenRef.current?.kill(); };
    /* Callback identities deliberately excluded (read via refs above) so the
       reveal tween only ever (re)starts on the fadeOut edge itself. */
  }, [fadeOut]);

  return <div ref={washRef} className="susdle-gameend-reset-fade" aria-hidden />;
}
